import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Check, CheckSquare, Loader2, Plus } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import {
  CATEGORIA_TAREFA_LABEL, PRIORIDADE_LABEL, STATUS_TAREFA_LABEL,
  type CategoriaTarefa, type PrioridadeTarefa,
} from '@/lib/types'
import { formatarData, hojeISO } from '@/lib/datas'
import { getMembros, type MembroComPerfil } from '@/services/equipe'
import { listTarefas, mudarStatusTarefa, type TarefaComRelacoes } from '@/services/tarefas'

type Aba = 'abertas' | 'concluidas' | 'todas'

export default function PendenciasPage() {
  const { current, user } = useAuth()
  const [tarefas, setTarefas] = useState<TarefaComRelacoes[]>([])
  const [membros, setMembros] = useState<MembroComPerfil[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [aba, setAba] = useState<Aba>('abertas')
  const [responsavel, setResponsavel] = useState<string>('todos')
  const [prioridade, setPrioridade] = useState<PrioridadeTarefa | 'todas'>('todas')
  const [categoria, setCategoria] = useState<CategoriaTarefa | 'todas'>('todas')

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    setErro(null)
    try {
      const [ts, ms] = await Promise.all([
        listTarefas(current.workspace.id, { aba, responsavelUserId: responsavel, prioridade, categoria }),
        getMembros(current.workspace.id),
      ])
      setTarefas(ts)
      setMembros(ms.filter(m => m.ativo && m.user_id))
    } catch {
      setErro('Não foi possível carregar as pendências. A migração phase3 já foi aplicada?')
    } finally {
      setLoading(false)
    }
  }, [current, aba, responsavel, prioridade, categoria])

  useEffect(() => { carregar() }, [carregar])

  const nomeMembro = useCallback(
    (userId: string | null) => membros.find(m => m.user_id === userId)?.perfil?.nome ?? null,
    [membros]
  )

  const hoje = hojeISO()
  const atrasadas = useMemo(
    () => tarefas.filter(t => t.prazo && t.prazo < hoje && !['concluida', 'cancelada'].includes(t.status)),
    [tarefas, hoje]
  )

  async function concluirRapido(t: TarefaComRelacoes) {
    if (!current) return
    try {
      await mudarStatusTarefa({
        workspaceId: current.workspace.id,
        tarefa: t,
        novoStatus: 'concluida',
        notificarUserId: t.criado_por && t.criado_por !== user?.id ? t.criado_por : null,
      })
      await carregar()
    } catch {
      setErro('Não foi possível concluir a pendência.')
    }
  }

  function CardTarefa({ t }: { t: TarefaComRelacoes }) {
    const aberta = !['concluida', 'cancelada'].includes(t.status)
    const atrasada = aberta && t.prazo !== null && t.prazo < hoje
    const responsavelNome = nomeMembro(t.responsavel_user_id)
    return (
      <div className="card" style={{ padding: '13px 16px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Link to={`/pendencias/nova?id=${t.id}`} style={{ fontWeight: 600, fontSize: 14.5 }}>
            {t.titulo}
          </Link>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span>{CATEGORIA_TAREFA_LABEL[t.categoria]}</span>
            {t.paciente_nome && t.paciente_id && (
              <Link to={`/pacientes/${t.paciente_id}`} style={{ color: 'var(--primary-deep)', fontWeight: 500 }}>
                · {t.paciente_nome}
              </Link>
            )}
            {responsavelNome && <span>· Resp.: {responsavelNome}</span>}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            <span className={`badge ${t.prioridade === 'urgente' ? 'badge-danger' : t.prioridade === 'alta' ? 'badge-warn' : 'badge-muted'}`}>
              {PRIORIDADE_LABEL[t.prioridade]}
            </span>
            <span className={`badge ${t.status === 'concluida' ? 'badge-ok' : t.status === 'cancelada' ? 'badge-muted' : 'badge-info'}`}>
              {STATUS_TAREFA_LABEL[t.status]}
            </span>
            {t.prazo && (
              <span className={`badge ${atrasada ? 'badge-danger' : 'badge-muted'}`}>
                {atrasada && <AlertTriangle size={11} aria-hidden />}
                {atrasada ? 'Atrasada — ' : 'Prazo '}{formatarData(t.prazo)}
              </span>
            )}
          </div>
        </div>
        {aberta && (
          <button
            className="btn-secondary"
            style={{ minHeight: 36, padding: '6px 12px', fontSize: 13, flexShrink: 0 }}
            onClick={() => concluirRapido(t)}
            aria-label={`Concluir ${t.titulo}`}
          >
            <Check size={14} aria-hidden /> Concluir
          </button>
        )}
      </div>
    )
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Pendências</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Tudo que exige uma ação — nada se perde.</p>
        </div>
        <Link to="/pendencias/nova" className="btn-primary">
          <Plus size={16} aria-hidden /> Nova pendência
        </Link>
      </header>

      {atrasadas.length > 0 && aba === 'abertas' && (
        <div className="badge badge-danger" style={{ marginBottom: 12, fontSize: 13, padding: '8px 12px' }}>
          <AlertTriangle size={13} aria-hidden /> {atrasadas.length} pendência{atrasadas.length > 1 ? 's' : ''} atrasada{atrasadas.length > 1 ? 's' : ''}
        </div>
      )}

      <div className="card" style={{ padding: 12, marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['abertas', 'concluidas', 'todas'] as Aba[]).map(a => (
          <button
            key={a} onClick={() => setAba(a)} aria-pressed={aba === a}
            className="badge"
            style={{
              cursor: 'pointer', fontSize: 13, padding: '7px 13px', border: '1px solid', textTransform: 'capitalize',
              background: aba === a ? 'var(--primary-soft)' : 'var(--card)',
              color: aba === a ? 'var(--primary-deep)' : 'var(--text-secondary)',
              borderColor: aba === a ? 'var(--primary)' : 'var(--border-input)',
            }}
          >
            {a === 'concluidas' ? 'Encerradas' : a}
          </button>
        ))}
        <select className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 13, marginLeft: 'auto' }} value={responsavel} onChange={e => setResponsavel(e.target.value)} aria-label="Filtrar por responsável">
          <option value="todos">Todos os responsáveis</option>
          {user && <option value={user.id}>Minhas</option>}
          {membros.filter(m => m.user_id !== user?.id).map(m => (
            <option key={m.user_id} value={m.user_id!}>{m.perfil?.nome ?? m.convite_email}</option>
          ))}
        </select>
        <select className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 13 }} value={prioridade} onChange={e => setPrioridade(e.target.value as typeof prioridade)} aria-label="Filtrar por prioridade">
          <option value="todas">Toda prioridade</option>
          {(['urgente', 'alta', 'normal', 'baixa'] as PrioridadeTarefa[]).map(p => (
            <option key={p} value={p}>{PRIORIDADE_LABEL[p]}</option>
          ))}
        </select>
        <select className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 13 }} value={categoria} onChange={e => setCategoria(e.target.value as typeof categoria)} aria-label="Filtrar por categoria">
          <option value="todas">Toda categoria</option>
          {(Object.keys(CATEGORIA_TAREFA_LABEL) as CategoriaTarefa[]).map(c => (
            <option key={c} value={c}>{CATEGORIA_TAREFA_LABEL[c]}</option>
          ))}
        </select>
      </div>

      {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
          <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
        </div>
      ) : tarefas.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <CheckSquare size={26} aria-hidden style={{ marginBottom: 8 }} />
          <div style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>
            {aba === 'abertas' ? 'Nenhuma pendência aberta — tudo em dia!' : 'Nada por aqui.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tarefas.map(t => <CardTarefa key={t.id} t={t} />)}
        </div>
      )}
    </div>
  )
}
